import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types'
import { t, getVatPriceProps } from '@insight/toolkit-utils'
import Currency from '../Currency/Currency'
import Button from '../Button/Button'
import Modal from '../Modal/Modal'
import ButtonGroup from '../ButtonGroup/ButtonGroup'
import AddToCartModalResource from './AddToCartModalResource'
import { ProductDetailTabAccessories } from '../PDPTabs/ProductDetailTabAccessories'
import {Skeleton} from "../Skeleton/Skeleton";

const AddToCartAccessoryModal = ({
  products,
  quantity,
  accessories,
  onClose,
  addToCart,
  continueCheckoutHandler,
  currencyCode,
  stock,
  permissions,
  showQuantityExceed,
  selectedWarranty,
  selectedCarrier,
  enabledWarranty,
  hasWarranties,
  showDividers,
  showPrice,
  categoryType,
  showVAT,
  asyncPrice,
  selectedContract,
  richRelevanceSSEComplete,
  isIPSUserWithContract
}) => {
  const [slide, setSlide] = useState({});
  const { exclVatProps, inclVatProps } = getVatPriceProps(showVAT);
  const enhanceRecommendedAccessoriesLDFlag = window?.flags?.['GNA-14878-Enhance-Recommended-Accessories'];
  useEffect(() => {
    let setSlideState
    if (!!enabledWarranty || !hasWarranties || !permissions.enable_warranty_purchase) {
      setSlide({ left: 0 })
    } else {
      setSlideState = setTimeout(() => {
        setSlide({ left: 0, transition: 'all .5s ease' })
      }, 100)
    }
    return () => {
      clearTimeout(setSlideState)
      setSlideState = null
    }
  }, [enabledWarranty])
  let subTotal = 0;
  let subTotalInclVat = 0;

  const renderProducts = () => {
    if (!products && !products.length) return null
    const productList = [...products]
    selectedCarrier && productList.push({ ...selectedCarrier, isCarrierProvider: true})
    Object.keys(selectedWarranty).length > 0 && productList.push({ ...selectedWarranty, isWarrantyProduct: true })
    return productList.map((product, index) => {
      // Needed because the placement API returns a difference product structure
      const name = product?.descriptions?.shortDescription || product?.description || product?.name;
      const price = (index === 0 ? selectedContract?.webPrice : null) || product?.price?.webPrice || product?.price?.price;
      const asyncItem = !!asyncPrice ? asyncPrice[product.materialId] : null;
      const finalPrice = (product?.isCarrierProvider) ? product?.price?.webPrice: !!price ? price : asyncItem?.webPrice;
      const priceInclVat = product?.price?.webPriceInclVat || product?.price?.priceInclVat;
      const image =
        (product?.images &&
          product?.images[0] &&
          product?.images[0]?.images &&
          product?.images[0]?.images.MD &&
          product?.images[0]?.images?.MD[0] &&
          product?.images[0]?.images?.MD[0]?.url) ||
        product.image
      const mrfPart =
        product.manufacturerPartNumber || product?.manufacturer?.partNumber
      const isWarranty = !!product?.isWarrantyProduct;
      const isCarrier = !!product?.isCarrierProvider;
      // increment subTotal
      subTotal += finalPrice * quantity;
      subTotalInclVat += priceInclVat * quantity;

      return (
        <AddToCartModalResource
          key={product.materialId}
          categoryType={product.categoryType}
          currencyCode={currencyCode}
          hideImage={!image}
          imageUrl={image}
          imageDescription={name}
          shortDescription={name}
          insightPart={product.materialId}
          mfrPart={mrfPart}
          quantity={quantity}
          stock={stock}
          price={finalPrice}
          priceInclVat={priceInclVat}
          showDividers={showDividers}
          showQuantityExceed={showQuantityExceed}
          isWarranty={isWarranty}
          isCarrier={isCarrier}
          showPrice={showPrice}
          showVAT={showVAT}
          asyncPrice={asyncPrice}
        />
      )
    })
  }

  const renderPrice = () => {
    if (!showVAT) {
      return <Currency currencyCode={currencyCode} value={subTotal} {...exclVatProps} />
    }

    return (
      <div className='c-add-item-to-cart__product__price__subtotal-wrapper'>
        <Currency currencyCode={currencyCode} value={subTotal} {...exclVatProps} />
        <div className='c-add-item-to-cart__inc-vat-wrapper'>
          <Currency currencyCode={currencyCode} value={subTotalInclVat} {...inclVatProps} />
        </div>
      </div>
    )
  }

  return (
    <Modal
      className={cn("c-add-item-to-cart", {
        "c-add-item-to-cart__vat": showVAT
      })}
      isOpen
      onClose={onClose}
      size="medium"
    >
      <div className="c-add-item-to-cart__accessory">
        <div className="c-add-item-to-cart__accessory__content" style={slide}>
          <Modal.Header onClick={onClose}>
            <h3>{t('Added to cart')}</h3>
          </Modal.Header>
          <Modal.Body id="added-to-cart-accessory-modal" data-testid="added-to-cart-accessory-modal">
            {renderProducts()}
            {showPrice &&
              <p className="o-grid o-grid--justify-right c-add-item-to-cart__product__price">
                <div className={cn("c-add-item-to-cart__product__price__subtotal", {
                  'c-add-item-to-cart__product__price__subtotal-vat': showVAT
                })}>
                  {t('Subtotal')}:&ensp;
                  {renderPrice()}
                </div>
              </p>
            }
            <ButtonGroup
              align="right"
              className="c-add-item-to-cart__button-group"
            >
              <Button
                size="small"
                color="secondary"
                onClick={onClose}
                aria-label={t('Continue Shopping')}
              >
                {t('Continue Shopping')}
              </Button>
              <Button
                size="small"
                color="primary"
                onClick={continueCheckoutHandler}
                aria-label={t('Continue to checkout')}
              >
                {t('Continue to checkout')}
              </Button>
            </ButtonGroup>


            {
                !richRelevanceSSEComplete && <div>
                  <h4 className="c-accessories-tab__title">
                    {t('Add to your purchase')}
                  </h4>
                  <Skeleton height="50px" width="100%" count={4} />
                </div>
            }

            {!!accessories?.length && permissions.enable_accessory_purchase && (
              <>
                {!enhanceRecommendedAccessoriesLDFlag && <h4 className="c-accessories-tab__title">
                  {t('Add to your purchase')}
                </h4>}
                <ProductDetailTabAccessories
                  accessories={accessories}
                  addToCart={addToCart}
                  showPrice={showPrice}
                  categoryType={categoryType}
                  showVAT={showVAT}
                  asyncPrice={asyncPrice}
                  richRelevanceSSEComplete={richRelevanceSSEComplete}
                  isIPSUserWithContract={isIPSUserWithContract}
                />
              </>
            )}
          </Modal.Body>
        </div>
      </div>
    </Modal>
  )
}

export default AddToCartAccessoryModal

AddToCartAccessoryModal.propTypes = {
  products: PropTypes.array,
  quantity: PropTypes.string,
  accessories: PropTypes.array,
  onClose: PropTypes.func,
  addToCart: PropTypes.func,
  continueCheckoutHandler: PropTypes.func,
  currencyCode: PropTypes.string,
  stock: PropTypes.string,
  permissions: PropTypes.object,
  showQuantityExceed: PropTypes.bool,
  selectedWarranty: PropTypes.object,
  showDividers: PropTypes.bool,
  showPrice: PropTypes.bool
}

AddToCartAccessoryModal.defaultProps = {
  showDividers: false,
  showPrice: true
}
